// src/pages/corretores/Corretores.tsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../services/apiService';
import { Corretor } from '../../data/Corretor';
import { ListaPaginada } from '../../data/ListaPaginada';
import Pagination from '../../componets/Pagination';
import ConfirmationModal from '../../componets/ConfirmationModal';

function Corretores() {
  const [data, setData] = useState<ListaPaginada<Corretor> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [corretorToDelete, setCorretorToDelete] = useState<number | null>(null);
  const itemsPerPage = 10; // Itens por página

  useEffect(() => {
    const fetchCorretores = async () => {
      setLoading(true);
      try {
        const endpoint = `/usuarios/corretores?pagina=${currentPage}&tamanhoPagina=${itemsPerPage}`;
        const responseData = await apiRequest<ListaPaginada<Corretor>>(endpoint, 'GET', true);
        setData(responseData);
      } catch (err) {
        setError((err as Error).message || 'Erro ao carregar os corretores.');
      } finally {
        setLoading(false);
      }
    };
    fetchCorretores();
  }, [currentPage]);

  const totalRecords = data?.totalItens ?? 0;
  const totalPages = data?.totalPaginas ?? 0;
  const currentItems = data?.itens ?? [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Abre o modal de confirmação
  const handleDeleteClick = (id: number) => {
    setCorretorToDelete(id);
    setShowDeleteModal(true);
  };

  // Executa a exclusão após a confirmação
  const confirmDelete = async () => {
    if (!corretorToDelete) return;

    setShowDeleteModal(false);
    // Poderíamos usar um estado de loading específico para a exclusão,
    // mas por simplicidade vamos reusar o principal.
    setLoading(true); 
    setError(null);

    try {
      // Assumindo que a API segue o padrão RESTful para exclusão
      await apiRequest(`usuarios/deletecorretor/${corretorToDelete}`, 'POST', true);

      // Recarrega os dados da página atual para refletir a exclusão
      const fetchCorretores = async () => {
        const endpoint = `/usuarios/corretores?pagina=${currentPage}&tamanhoPagina=${itemsPerPage}`;
        const responseData = await apiRequest<ListaPaginada<Corretor>>(endpoint, 'GET', true);
        setData(responseData);
      };
      fetchCorretores();
    } catch (err) {
      setError((err as Error).message || 'Erro ao excluir o corretor.');
    } finally {
      setLoading(false);
      setCorretorToDelete(null);
    }
  };

  if (loading) {
    return <p>Carregando Corretores...</p>;
  }

  if (error) {
    return <p>Erro: {error}</p>;
  }

  return (
    <div className="row">
      <div className="card">
        <div className="card-body">
          <div className="mb-3">
            <h1 className="h3 d-inline align-middle">Corretores</h1>
          </div>
          <div className="mb-3 text-end">
            <Link to="/corretores/novo" className="btn btn-primary"> Novo <i className="bi bi-plus"></i></Link>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th className="col-2">#</th>
                <th className="col">Nome</th>
                <th className="col">Creci</th>
                <th className="col-3">Ações</th>
              </tr>
            </thead>
            <tbody>
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center">Nenhum corretor cadastrado.</td>
              </tr>
            ) : (currentItems.map(corretor => (
              <tr key={corretor.id}>
                <td>{corretor.id}</td>
                <td>{corretor.nome}</td>
                <td>{corretor.creci}</td>
                <td>
                  <div className="d-flex gap-4">
                    <Link to={`/corretores/editar/${corretor.id}`} className="btn btn-outline-primary"><i className="align-middle bi bi-pencil-square"></i> Editar</Link>
                    <button onClick={() => handleDeleteClick(corretor.id!)} className="btn btn-outline-danger">
                      <i className="align-middle bi bi-trash"></i> Excluir
                    </button>
                  </div>
                </td>
              </tr>
              ))
            )}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
          />
          <ConfirmationModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title="Confirmar Exclusão"
            body="Tem certeza que deseja excluir este corretor? Esta ação não pode ser desfeita."
            confirmText="Excluir"
            confirmButtonVariant="danger"
          />
        </div>
      </div>
    </div>
  );
}

export default Corretores;